import React, { useCallback, useMemo, ReactElement } from "react";
import {
  StyledFiberManualRecordIcon,
  StyledIndicatorIconButton,
  StyledIndicators,
} from "./Styled";
import { element, object, bool, func, number } from "prop-types";

interface IndicatorsProps {
  IndicatorIcon?: ReactElement | ReactElement[];
  length: number;
  active: number;
  press: (index: number) => void;
  indicatorIconButtonProps: Record<string, any>;
  activeIndicatorIconButtonProps: Record<string, any>;
  indicatorContainerProps: Record<string, any>;
}

export const Indicators = (props: IndicatorsProps) => {
  const IndicatorIcon = useMemo(
    () =>
      props.IndicatorIcon !== undefined ? (
        props.IndicatorIcon
      ) : (
        <StyledFiberManualRecordIcon />
      ),
    [props.IndicatorIcon],
  );

  const completeListIfRequired = useCallback(
    (arrayOfIcons: ReactElement[]) => {
      while (arrayOfIcons.length < props.length) {
        let index = 0;
        arrayOfIcons.push(arrayOfIcons[index]);
      }
    },
    [props.length],
  );

  const {
    className: indicatorIconButtonClass,
    style: indicatorIconButtonStyle,
    ...indicatorIconButtonProps
  } = props.indicatorIconButtonProps;
  const {
    className: activeIndicatorIconButtonClass,
    style: activeIndicatorIconButtonStyle,
    ...activeIndicatorIconButtonProps
  } = props.activeIndicatorIconButtonProps;

  const indicators = [];

  for (let i = 0; i < props.length; i++) {
    const className =
      i === props.active
        ? `${indicatorIconButtonClass} ${activeIndicatorIconButtonClass}`
        : `${indicatorIconButtonClass}`;

    const style =
      i === props.active
        ? { ...indicatorIconButtonStyle, ...activeIndicatorIconButtonStyle }
        : indicatorIconButtonStyle;

    const restProps =
      i === props.active
        ? { ...indicatorIconButtonProps, ...activeIndicatorIconButtonProps }
        : indicatorIconButtonProps;

    if (restProps["aria-label"] === undefined) {
      restProps["aria-label"] = "carousel indicator";
    }

    const createIndicator = (icon: ReactElement) => (
      <StyledIndicatorIconButton
        $active={i === props.active}
        key={i}
        className={className}
        style={style}
        onClick={() => props.press(i)}
        {...restProps}
        aria-label={`${restProps["aria-label"]} ${i + 1}`}
      >
        {icon}
      </StyledIndicatorIconButton>
    );

    if (Array.isArray(IndicatorIcon)) {
      indicators.push(createIndicator(IndicatorIcon[i]));
      completeListIfRequired(IndicatorIcon);
    } else {
      indicators.push(createIndicator(IndicatorIcon));
    }
  }

  const {
    className: indicatorContainerClass,
    style: indicatorContainerStyle,
    ...indicatorContainerProps
  } = props.indicatorContainerProps;

  return (
    <StyledIndicators
      className={indicatorContainerClass}
      style={indicatorContainerStyle}
      {...indicatorContainerProps}
    >
      {indicators}
    </StyledIndicators>
  );
};

Indicators.propTypes = {
  IndicatorIcon: element,
  indicatorIconButtonProps: object,
  indicatorContainerProps: object,
  active: bool,
  press: func.isRequired,
  activeIndicatorIconButtonProps: object,
  length: number,
};
